import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "../../lib/api/client.js";
import { useMe } from "../../hooks/useMe.js";
import {
  createTurnoSchema,
  updateTurnoSchema,
  createSquadraSchema,
  createZonaSchema,
  updateZonaSchema,
  createMezzoSchema,
  type Turno,
  type CreateTurno,
  type UpdateTurno,
  type CreateSquadra,
  type Zona,
  type CreateZona,
  type Mezzo,
  type CreateMezzo,
  type FrazioneMerceologica,
} from "@taras/contracts";

// ─── Types locali ─────────────────────────────────────────────────────────────

type Operatore = { id: string; nome: string; cognome: string; username: string };
type SquadraMembro = { operatore: Operatore };
type Squadra = { id: string; nome: string; attiva: boolean; operatori: SquadraMembro[] };

// ─── Config ───────────────────────────────────────────────────────────────────

const FRAZIONI: { value: FrazioneMerceologica; label: string; bg: string; border: string; color: string }[] = [
  { value: "ORGANICO",         label: "Organico",         bg: "bg-amber-100",  border: "border-amber-400",  color: "text-amber-800" },
  { value: "CARTA",            label: "Carta",            bg: "bg-blue-100",   border: "border-blue-400",   color: "text-blue-800" },
  { value: "PLASTICA_METALLO", label: "Plastica/Metallo", bg: "bg-yellow-100", border: "border-yellow-400", color: "text-yellow-800" },
  { value: "VETRO",            label: "Vetro",            bg: "bg-green-100",  border: "border-green-400",  color: "text-green-800" },
  { value: "SECCO_RESIDUO",    label: "Secco residuo",    bg: "bg-gray-100",   border: "border-gray-400",   color: "text-gray-700" },
];

const STATI_TURNO = [
  { value: "PIANIFICATO", label: "Pianificato", cls: "bg-gray-100 text-gray-600" },
  { value: "IN_CORSO",    label: "In corso",    cls: "bg-green-100 text-green-700" },
  { value: "CHIUSO",      label: "Chiuso",      cls: "bg-blue-100 text-blue-700" },
  { value: "ANNULLATO",   label: "Annullato",   cls: "bg-red-100 text-red-600" },
] as const;

const STATO_CFG = Object.fromEntries(STATI_TURNO.map((s) => [s.value, s])) as Record<string, { label: string; cls: string }>;

function FrazBadge({ f }: { f: FrazioneMerceologica }) {
  const cfg = FRAZIONI.find((x) => x.value === f);
  if (!cfg) return null;
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${cfg.bg} ${cfg.border} ${cfg.color}`}>{cfg.label}</span>;
}

// ─── Schema form squadra-turno ────────────────────────────────────────────────

const createTurnoSquadraSchema = createTurnoSchema
  .omit({ operatore_id: true })
  .extend({ squadra_id: z.string().uuid("Seleziona una squadra") });
type CreateTurnoSquadra = z.infer<typeof createTurnoSquadraSchema>;

// ─── Pulsanti frazione (riutilizzabili) ───────────────────────────────────────

function FrazionePicker({ value, onChange }: { value?: string; onChange: (v: FrazioneMerceologica) => void }) {
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {FRAZIONI.map((f) => {
        const sel = value === f.value;
        return (
          <button
            key={f.value}
            type="button"
            onClick={() => onChange(f.value)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
              sel ? `${f.bg} ${f.border} ${f.color} ring-2 ring-offset-1 ring-blue-400` : "border-gray-200 text-gray-600 hover:border-gray-300"
            }`}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Pannello Squadre ─────────────────────────────────────────────────────────

function SquadrePanel({ operatori }: { operatori: Operatore[] | undefined }) {
  const [open, setOpen] = useState(false);
  const [selectedSquadra, setSelectedSquadra] = useState<string | null>(null);
  const [addingOp, setAddingOp] = useState("");
  const qc = useQueryClient();

  const { data: squadre } = useQuery<Squadra[]>({
    queryKey: ["squadre"],
    queryFn: () => apiClient.get("/api/v1/turni/squadre"),
  });

  const createSquadra = useMutation({
    mutationFn: (body: CreateSquadra) => apiClient.post<Squadra>("/api/v1/turni/squadre", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["squadre"] }),
  });
  const addOp = useMutation({
    mutationFn: ({ squadraId, operatoreId }: { squadraId: string; operatoreId: string }) =>
      apiClient.post(`/api/v1/turni/squadre/${squadraId}/operatori`, { operatore_id: operatoreId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["squadre"] }); setAddingOp(""); },
  });
  const removeOp = useMutation({
    mutationFn: ({ squadraId, operatoreId }: { squadraId: string; operatoreId: string }) =>
      apiClient.delete(`/api/v1/turni/squadre/${squadraId}/operatori/${operatoreId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["squadre"] }),
  });

  const squadraForm = useForm<CreateSquadra>({ resolver: zodResolver(createSquadraSchema) });
  const sel = squadre?.find((s) => s.id === selectedSquadra);

  return (
    <>
      <button onClick={() => setOpen(true)} className={btnOutline}>Squadre</button>
      {open && (
        <SidePanel title="Squadre" onClose={() => setOpen(false)}>
          <div className="flex flex-1 overflow-hidden">
            <div className="w-52 border-r border-gray-200 flex flex-col">
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {squadre?.map((s) => (
                  <button key={s.id} onClick={() => setSelectedSquadra(s.id)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 ${selectedSquadra === s.id ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"}`}>
                    {s.nome} <span className="text-xs text-gray-400">({s.operatori.length})</span>
                  </button>
                ))}
                {(!squadre || squadre.length === 0) && <p className="px-4 py-6 text-xs text-gray-400 italic">Nessuna squadra.</p>}
              </div>
              <form className="border-t p-3 space-y-1" onSubmit={squadraForm.handleSubmit((d) => createSquadra.mutate(d))}>
                <input {...squadraForm.register("nome")} placeholder="Nome squadra…" className={inputXs} />
                <button type="submit" disabled={createSquadra.isPending} className={btnXs}>+ Crea</button>
              </form>
            </div>
            <div className="flex-1 p-5 overflow-y-auto">
              {!sel ? <p className="text-sm text-gray-400 italic">Seleziona una squadra.</p> : (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800">{sel.nome}</h4>
                  <div className="space-y-1">
                    {sel.operatori.length === 0 && <p className="text-xs text-gray-400 italic">Nessun operatore.</p>}
                    {sel.operatori.map(({ operatore: op }) => (
                      <div key={op.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <span className="text-sm text-gray-700">{op.cognome} {op.nome}</span>
                        <button onClick={() => removeOp.mutate({ squadraId: sel.id, operatoreId: op.id })} className="text-xs text-red-500 hover:text-red-700">Rimuovi</button>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-600">Aggiungi operatore</p>
                    <select value={addingOp} onChange={(e) => setAddingOp(e.target.value)} className={inputSm}>
                      <option value="">— seleziona —</option>
                      {operatori?.filter((o) => !sel.operatori.some((m) => m.operatore.id === o.id))
                        .map((o) => <option key={o.id} value={o.id}>{o.cognome} {o.nome}</option>)}
                    </select>
                    <button onClick={() => addingOp && addOp.mutate({ squadraId: sel.id, operatoreId: addingOp })}
                      disabled={!addingOp || addOp.isPending} className={`w-full text-sm py-1.5 rounded disabled:opacity-40 ${btnBlue}`}>
                      Aggiungi
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SidePanel>
      )}
    </>
  );
}

// ─── Pannello Zone ────────────────────────────────────────────────────────────

function ZonePanel() {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: zone } = useQuery<Zona[]>({
    queryKey: ["zone"],
    queryFn: () => apiClient.get("/api/v1/turni/zone"),
    staleTime: 30_000,
  });

  const createForm = useForm<CreateZona>({ resolver: zodResolver(createZonaSchema) });
  const editForm = useForm<Partial<CreateZona>>({ resolver: zodResolver(updateZonaSchema) });

  const create = useMutation({
    mutationFn: (body: CreateZona) => apiClient.post<Zona>("/api/v1/turni/zone", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["zone"] }); createForm.reset(); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CreateZona> }) =>
      apiClient.patch<Zona>(`/api/v1/turni/zone/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["zone"] }); setEditId(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/turni/zone/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zone"] }),
  });

  const startEdit = (z: Zona) => {
    setEditId(z.id);
    editForm.reset({ nome: z.nome, codice: z.codice });
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className={btnOutline}>Zone</button>
      {open && (
        <SidePanel title="Zone di raccolta" onClose={() => setOpen(false)}>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {/* Nuova zona */}
            <form onSubmit={createForm.handleSubmit((d) => create.mutate(d))} className="flex gap-2 items-end border-b pb-4 mb-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Nome</label>
                <input {...createForm.register("nome")} placeholder="es. Centro" className={inputSm} />
                {createForm.formState.errors.nome && <p className="text-xs text-red-500 mt-0.5">{createForm.formState.errors.nome.message}</p>}
              </div>
              <div className="w-28">
                <label className="block text-xs text-gray-500 mb-1">Codice</label>
                <input {...createForm.register("codice")} placeholder="Z001" className={inputSm} />
              </div>
              <button type="submit" disabled={create.isPending} className={`${btnBlue} px-3 py-2 text-sm rounded-lg`}>
                + Aggiungi
              </button>
            </form>
            {/* Lista */}
            {zone?.map((z) =>
              editId === z.id ? (
                <form key={z.id} onSubmit={editForm.handleSubmit((d) => update.mutate({ id: z.id, body: d }))} className="flex gap-2 items-center bg-blue-50 rounded-lg p-2">
                  <input {...editForm.register("nome")} className={`${inputSm} flex-1`} />
                  <input {...editForm.register("codice")} className={`${inputSm} w-24`} />
                  <button type="submit" className="text-xs text-blue-600 hover:underline">Salva</button>
                  <button type="button" onClick={() => setEditId(null)} className="text-xs text-gray-500 hover:underline">Annulla</button>
                </form>
              ) : (
                <div key={z.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-gray-800">{z.nome}</span>
                    <span className="ml-2 text-xs font-mono text-gray-500">{z.codice}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(z)} className="text-xs text-blue-600 hover:underline">Modifica</button>
                    <button onClick={() => remove.mutate(z.id)} className="text-xs text-red-500 hover:underline">Elimina</button>
                  </div>
                </div>
              )
            )}
            {(!zone || zone.length === 0) && <p className="text-sm text-gray-400 italic">Nessuna zona.</p>}
          </div>
        </SidePanel>
      )}
    </>
  );
}

// ─── Pannello Mezzi ───────────────────────────────────────────────────────────

function MezziPanel() {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: mezzi } = useQuery<Mezzo[]>({
    queryKey: ["mezzi"],
    queryFn: () => apiClient.get("/api/v1/turni/mezzi"),
    staleTime: 30_000,
  });

  const createForm = useForm<CreateMezzo>({ resolver: zodResolver(createMezzoSchema) });
  const editForm = useForm<{ targa?: string; descrizione?: string }>({});

  const create = useMutation({
    mutationFn: (body: CreateMezzo) => apiClient.post<Mezzo>("/api/v1/turni/mezzi", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mezzi"] }); createForm.reset(); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { targa?: string; descrizione?: string } }) =>
      apiClient.patch<Mezzo>(`/api/v1/turni/mezzi/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mezzi"] }); setEditId(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/turni/mezzi/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mezzi"] }),
  });

  const startEdit = (m: Mezzo) => {
    setEditId(m.id);
    editForm.reset({ targa: m.targa, descrizione: m.descrizione ?? "" });
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className={btnOutline}>Mezzi</button>
      {open && (
        <SidePanel title="Catalogo mezzi" onClose={() => setOpen(false)}>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            <form onSubmit={createForm.handleSubmit((d) => create.mutate(d))} className="flex gap-2 items-end border-b pb-4 mb-2">
              <div className="w-32">
                <label className="block text-xs text-gray-500 mb-1">Targa</label>
                <input {...createForm.register("targa")} placeholder="TA-000-AA" className={inputSm} />
                {createForm.formState.errors.targa && <p className="text-xs text-red-500 mt-0.5">{createForm.formState.errors.targa.message}</p>}
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Descrizione (opz.)</label>
                <input {...createForm.register("descrizione")} placeholder="Compattatore…" className={inputSm} />
              </div>
              <button type="submit" disabled={create.isPending} className={`${btnBlue} px-3 py-2 text-sm rounded-lg`}>
                + Aggiungi
              </button>
            </form>
            {mezzi?.map((m) =>
              editId === m.id ? (
                <form key={m.id} onSubmit={editForm.handleSubmit((d) => update.mutate({ id: m.id, body: d }))} className="flex gap-2 items-center bg-blue-50 rounded-lg p-2">
                  <input {...editForm.register("targa")} className={`${inputSm} w-28`} />
                  <input {...editForm.register("descrizione")} className={`${inputSm} flex-1`} />
                  <button type="submit" className="text-xs text-blue-600 hover:underline">Salva</button>
                  <button type="button" onClick={() => setEditId(null)} className="text-xs text-gray-500 hover:underline">Annulla</button>
                </form>
              ) : (
                <div key={m.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-mono font-medium text-gray-800">{m.targa}</span>
                    {m.descrizione && <span className="ml-2 text-xs text-gray-500">{m.descrizione}</span>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(m)} className="text-xs text-blue-600 hover:underline">Modifica</button>
                    <button onClick={() => remove.mutate(m.id)} className="text-xs text-red-500 hover:underline">Rimuovi</button>
                  </div>
                </div>
              )
            )}
            {(!mezzi || mezzi.length === 0) && <p className="text-sm text-gray-400 italic">Nessun mezzo.</p>}
          </div>
        </SidePanel>
      )}
    </>
  );
}

// ─── Modal edit turno ─────────────────────────────────────────────────────────

function EditTurnoModal({
  turno,
  zone,
  mezzi,
  onClose,
}: {
  turno: Turno;
  zone: Zona[] | undefined;
  mezzi: Mezzo[] | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useForm<UpdateTurno>({
    resolver: zodResolver(updateTurnoSchema),
    defaultValues: {
      zona_id: turno.zona_id,
      frazione_merceologica: turno.frazione_merceologica,
      data_turno: turno.data_turno.slice(0, 10),
      mezzo_targa: turno.mezzo_targa ?? "",
      stato: turno.stato,
    },
  });

  const save = useMutation({
    mutationFn: (body: UpdateTurno) => apiClient.patch<Turno>(`/api/v1/turni/${turno.id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["turni"] }); onClose(); },
  });

  const del = useMutation({
    mutationFn: () => apiClient.delete(`/api/v1/turni/${turno.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["turni"] }); onClose(); },
  });

  return (
    <Modal title="Modifica turno" onClose={onClose}>
      <form onSubmit={form.handleSubmit((d) => save.mutate(d))} className="space-y-3">
        <Field label="Zona" error={form.formState.errors.zona_id?.message}>
          <select {...form.register("zona_id")} className={input}>
            <option value="">— seleziona —</option>
            {zone?.map((z) => <option key={z.id} value={z.id}>{z.nome} ({z.codice})</option>)}
          </select>
        </Field>
        <Field label="Frazione merceologica">
          <FrazionePicker value={form.watch("frazione_merceologica")} onChange={(v) => form.setValue("frazione_merceologica", v, { shouldValidate: true })} />
        </Field>
        <Field label="Data" error={form.formState.errors.data_turno?.message}>
          <input type="date" {...form.register("data_turno")} className={input} />
        </Field>
        <Field label="Targa mezzo">
          <select {...form.register("mezzo_targa")} className={input}>
            <option value="">— nessuno —</option>
            {mezzi?.map((m) => <option key={m.id} value={m.targa}>{m.targa}{m.descrizione ? ` — ${m.descrizione}` : ""}</option>)}
          </select>
        </Field>
        <Field label="Stato">
          <select {...form.register("stato")} className={input}>
            {STATI_TURNO.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        {save.isError && <p className="text-xs text-red-600">Errore durante il salvataggio.</p>}
        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={() => { if (confirm("Eliminare questo turno?")) del.mutate(); }}
            className="text-xs text-red-500 hover:text-red-700">
            Elimina turno
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={btnCancel}>Annulla</button>
            <button type="submit" disabled={save.isPending} className={`${btnBlue} px-4 py-2 text-sm rounded-lg disabled:opacity-50`}>
              {save.isPending ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export function TurniPage() {
  const [openNew, setOpenNew] = useState(false);
  const [editTurno, setEditTurno] = useState<Turno | null>(null);
  const [modoCreazione, setModoCreazione] = useState<"singolo" | "squadra">("singolo");
  const [filterStato, setFilterStato] = useState("");
  const [filterDa, setFilterDa] = useState("");
  const [filterA, setFilterA] = useState("");
  const qc = useQueryClient();
  const { data: me } = useMe();
  const canWrite = me?.ruolo !== "VISUALIZZATORE";

  const params = new URLSearchParams({ limit: "50" });
  if (filterStato) params.set("stato", filterStato);
  if (filterDa) params.set("data_da", filterDa);
  if (filterA) params.set("data_a", filterA);

  const { data, isLoading } = useQuery<{ items: Turno[]; nextCursor: string | null }>({
    queryKey: ["turni", filterStato, filterDa, filterA],
    queryFn: () => apiClient.get(`/api/v1/turni?${params}`),
  });

  const { data: zone } = useQuery<Zona[]>({
    queryKey: ["zone"],
    queryFn: () => apiClient.get("/api/v1/turni/zone"),
    staleTime: 60_000,
  });

  const { data: operatori } = useQuery<Operatore[]>({
    queryKey: ["operatori"],
    queryFn: () => apiClient.get("/api/v1/turni/operatori"),
    staleTime: 60_000,
  });

  const { data: squadre } = useQuery<Squadra[]>({
    queryKey: ["squadre"],
    queryFn: () => apiClient.get("/api/v1/turni/squadre"),
    staleTime: 30_000,
  });

  const { data: mezzi } = useQuery<Mezzo[]>({
    queryKey: ["mezzi"],
    queryFn: () => apiClient.get("/api/v1/turni/mezzi"),
    staleTime: 60_000,
  });

  const singoloForm = useForm<CreateTurno>({
    resolver: zodResolver(createTurnoSchema),
    defaultValues: { data_turno: new Date().toISOString().slice(0, 10) },
  });
  const squadraForm = useForm<CreateTurnoSquadra>({
    resolver: zodResolver(createTurnoSquadraSchema),
    defaultValues: { data_turno: new Date().toISOString().slice(0, 10) },
  });

  const closeNew = () => {
    setOpenNew(false);
    singoloForm.reset({ data_turno: new Date().toISOString().slice(0, 10) });
    squadraForm.reset({ data_turno: new Date().toISOString().slice(0, 10) });
  };

  const createSingolo = useMutation({
    mutationFn: (body: CreateTurno) => apiClient.post<Turno>("/api/v1/turni", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["turni"] }); closeNew(); },
  });
  const createSquadra = useMutation({
    mutationFn: (body: CreateTurnoSquadra) => apiClient.post<Turno[]>("/api/v1/turni/squadra", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["turni"] }); closeNew(); },
  });

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-end gap-4">
        <h2 className="text-lg font-semibold text-gray-900 self-center mr-2">Turni di raccolta</h2>
        <div><label className={labelSm}>Da</label><input type="date" value={filterDa} onChange={(e) => setFilterDa(e.target.value)} className={inputSm} /></div>
        <div><label className={labelSm}>A</label><input type="date" value={filterA} onChange={(e) => setFilterA(e.target.value)} className={inputSm} /></div>
        <div>
          <label className={labelSm}>Stato</label>
          <select value={filterStato} onChange={(e) => setFilterStato(e.target.value)} className={inputSm}>
            <option value="">Tutti</option>
            {STATI_TURNO.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        {(filterStato || filterDa || filterA) && (
          <button onClick={() => { setFilterStato(""); setFilterDa(""); setFilterA(""); }} className="text-xs text-gray-500 hover:text-gray-700 underline self-end pb-2">
            Azzera
          </button>
        )}
        {canWrite && (
          <div className="flex gap-2 ml-auto">
            <ZonePanel />
            <MezziPanel />
            <SquadrePanel operatori={operatori} />
            <button onClick={() => setOpenNew(true)} className={`${btnBlue} px-4 py-2 text-sm rounded-lg`}>
              Nuovo turno
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <p className="text-sm text-gray-500">Caricamento…</p>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className={th}>Data</th>
                  <th className={th}>Operatore</th>
                  <th className={th}>Zona</th>
                  <th className={th}>Frazione</th>
                  <th className={th}>Mezzo</th>
                  <th className={th}>Stato</th>
                  <th className={th}>Orario</th>
                  <th className={th}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.items.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400 italic">Nessun turno trovato.</td></tr>
                )}
                {data?.items.map((t) => {
                  const stato = STATO_CFG[t.stato] ?? { label: t.stato, cls: "" };
                  return (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{new Intl.DateTimeFormat("it-IT").format(new Date(t.data_turno))}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {t.operatore ? `${t.operatore.cognome} ${t.operatore.nome}` : <span className="text-gray-400 text-xs font-mono">{t.operatore_id.slice(0, 8)}…</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.zona?.nome ?? "—"}</td>
                      <td className="px-4 py-3"><FrazBadge f={t.frazione_merceologica} /></td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{t.mezzo_targa ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${stato.cls}`}>{stato.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {t.ora_inizio ? new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit" }).format(new Date(t.ora_inizio)) : "—"}
                        {t.ora_fine ? ` → ${new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit" }).format(new Date(t.ora_fine))}` : ""}
                      </td>
                      <td className="px-4 py-3">
                        {canWrite && (
                          <button onClick={() => setEditTurno(t)} className="text-xs text-blue-600 hover:underline">Modifica</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal nuovo turno */}
      {openNew && (
        <Modal title="Nuovo turno" onClose={closeNew}>
          {/* Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm mb-3">
            {(["singolo", "squadra"] as const).map((m) => (
              <button key={m} type="button" onClick={() => setModoCreazione(m)}
                className={`flex-1 py-2 font-medium transition-colors ${modoCreazione === m ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}>
                {m === "singolo" ? "Singolo operatore" : "Squadra"}
              </button>
            ))}
          </div>

          {modoCreazione === "singolo" && (
            <form onSubmit={singoloForm.handleSubmit((d) => createSingolo.mutate(d))} className="space-y-3">
              <Field label="Operatore" error={singoloForm.formState.errors.operatore_id?.message}>
                <select {...singoloForm.register("operatore_id")} className={input}>
                  <option value="">— seleziona —</option>
                  {operatori?.map((o) => <option key={o.id} value={o.id}>{o.cognome} {o.nome} ({o.username})</option>)}
                </select>
              </Field>
              <Field label="Zona" error={singoloForm.formState.errors.zona_id?.message}>
                <select {...singoloForm.register("zona_id")} className={input}>
                  <option value="">— seleziona —</option>
                  {zone?.map((z) => <option key={z.id} value={z.id}>{z.nome} ({z.codice})</option>)}
                </select>
              </Field>
              <Field label="Frazione merceologica">
                <FrazionePicker value={singoloForm.watch("frazione_merceologica")} onChange={(v) => singoloForm.setValue("frazione_merceologica", v, { shouldValidate: true })} />
                {singoloForm.formState.errors.frazione_merceologica && <p className="text-xs text-red-600 mt-1">{singoloForm.formState.errors.frazione_merceologica.message}</p>}
              </Field>
              <Field label="Data" error={singoloForm.formState.errors.data_turno?.message}>
                <input type="date" {...singoloForm.register("data_turno")} className={input} />
              </Field>
              <Field label="Targa mezzo">
                <select {...singoloForm.register("mezzo_targa")} className={input}>
                  <option value="">— nessuno —</option>
                  {mezzi?.map((m) => <option key={m.id} value={m.targa}>{m.targa}{m.descrizione ? ` — ${m.descrizione}` : ""}</option>)}
                </select>
              </Field>
              {createSingolo.isError && <p className="text-xs text-red-600">Errore. Verificare i dati.</p>}
              <FormButtons onCancel={closeNew} loading={createSingolo.isPending} label="Crea turno" />
            </form>
          )}

          {modoCreazione === "squadra" && (
            <form onSubmit={squadraForm.handleSubmit((d) => createSquadra.mutate(d))} className="space-y-3">
              <Field label="Squadra" error={squadraForm.formState.errors.squadra_id?.message}>
                <select {...squadraForm.register("squadra_id")} className={input}>
                  <option value="">— seleziona —</option>
                  {squadre?.map((s) => <option key={s.id} value={s.id}>{s.nome} ({s.operatori.length} operatori)</option>)}
                </select>
                {squadre?.length === 0 && <p className="text-xs text-amber-600 mt-1">Nessuna squadra. Creane una con "Squadre".</p>}
              </Field>
              <Field label="Zona" error={squadraForm.formState.errors.zona_id?.message}>
                <select {...squadraForm.register("zona_id")} className={input}>
                  <option value="">— seleziona —</option>
                  {zone?.map((z) => <option key={z.id} value={z.id}>{z.nome} ({z.codice})</option>)}
                </select>
              </Field>
              <Field label="Frazione merceologica">
                <FrazionePicker value={squadraForm.watch("frazione_merceologica")} onChange={(v) => squadraForm.setValue("frazione_merceologica", v, { shouldValidate: true })} />
              </Field>
              <Field label="Data" error={squadraForm.formState.errors.data_turno?.message}>
                <input type="date" {...squadraForm.register("data_turno")} className={input} />
              </Field>
              <Field label="Targa mezzo">
                <select {...squadraForm.register("mezzo_targa")} className={input}>
                  <option value="">— nessuno —</option>
                  {mezzi?.map((m) => <option key={m.id} value={m.targa}>{m.targa}{m.descrizione ? ` — ${m.descrizione}` : ""}</option>)}
                </select>
              </Field>
              {createSquadra.isError && <p className="text-xs text-red-600">Errore. Verificare i dati.</p>}
              <FormButtons onCancel={closeNew} loading={createSquadra.isPending} label="Crea turni" />
            </form>
          )}
        </Modal>
      )}

      {/* Modal edit turno */}
      {editTurno && (
        <EditTurnoModal
          turno={editTurno}
          zone={zone}
          mezzi={mezzi}
          onClose={() => setEditTurno(null)}
        />
      )}
    </div>
  );

}

// ─── Shared UI ────────────────────────────────────────────────────────────────

const input   = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inputSm = "px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inputXs = "w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500";
const labelSm = "block text-xs text-gray-500 mb-1";
const th      = "px-4 py-3 text-left font-medium text-gray-600";
const btnBlue   = "bg-blue-600 text-white hover:bg-blue-700 font-medium";
const btnOutline = "px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors";
const btnCancel = "px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50";
const btnXs   = "w-full text-xs py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400";

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function FormButtons({ onCancel, loading, label }: { onCancel: () => void; loading: boolean; label: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className={btnCancel}>Annulla</button>
      <button type="submit" disabled={loading} className={`${btnBlue} px-4 py-2 text-sm rounded-lg disabled:opacity-50`}>
        {loading ? "Salvataggio…" : label}
      </button>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SidePanel({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex bg-black/40">
      <div className="ml-auto bg-white w-full max-w-2xl shadow-xl flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <div className="flex flex-col flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
